import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import "../css/BioprojectComponent.css";
import { API_BASE_URL } from "../../config"; 
import { FaArrowLeft } from 'react-icons/fa';


const BioProjectComponent = () => {
  const [bioProjects, setBioProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const projectsPerPage = 10;

  // Filters
  const [selectedDataTypes, setSelectedDataTypes] = useState([]);
  const [selectedRelevance, setSelectedRelevance] = useState(null);
  const [organizationQuery, setOrganizationQuery] = useState("");

  // Detail view
  const [selectedProject, setSelectedProject] = useState(null);
  const navigate = useNavigate();

  // ---------------- PAGINATION ----------------
  const Pagination = ({ currentPage, totalCount, perPage, setCurrentPage }) => {
    const totalPages = Math.ceil(totalCount / perPage);
    if (totalPages <= 1) return null;

    return (
      <div className="pagination-controls">
         {/* 🔙 Back to Home Button */}
      
        <button className="pci-back-home" onClick={() => navigate('/')}>
          <FaArrowLeft className="pci-back-icon" /> Back to Home
        </button>
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
        >
          ← Prev
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
        >
          Next →
        </button>
      </div>
    );
  };

  // ---------------- FETCH DATA ----------------
  const fetchBioProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: searchTerm,
        page: currentPage,
        page_size: projectsPerPage,
        data_type: selectedDataTypes.join(","),
        organization: organizationQuery,
        ...(selectedRelevance !== null && { relevance: selectedRelevance ? "Yes" : "No" }),
      };
      const response = await axios.get(`http://197.156.76.122/api/bioproject/`, { params });
      //const response = await axios.get("http://127.0.0.1:8000/api/bioproject/", { params });
      setBioProjects(response.data.results || response.data);
      setTotalCount(response.data.count || response.data.length || 0);
    } catch (error) {
      console.error("Error fetching BioProjects:", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage, selectedDataTypes, selectedRelevance, organizationQuery]);

  useEffect(() => {
    fetchBioProjects();
  }, [fetchBioProjects]);

  // ---------------- HELPERS ----------------
  const shortDesc = (text) =>
    text && text.length > 150 ? text.slice(0, 147) + "..." : text;

  const formatDataType = (dt) =>
    dt
      ? dt
          .split(",")
          .map((d) => d.trim())
          .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
          .join(", ")
      : "";

  // ---------------- DETAIL VIEW ----------------
  const openProject = (project) => {
    setSelectedProject(project);
    window.scrollTo(0, 0);
    navigate(`/BioProject/${project.bio_project_accession}`);
  };

  const closeProject = () => {
    setSelectedProject(null);
    navigate("/BioProject");
  };

  if (selectedProject) {
    return (
      <div className="bioproject-page">
        
        <div className="fullpage-result">
          <button className="fullpage-back" onClick={closeProject}>
            ← Back
          </button>

          <h1>{selectedProject.project_title}</h1>
          <p>
            <strong>Accession:</strong>{" "}
            <Link to={`/BioProject/${selectedProject.bio_project_accession}`}>
              {selectedProject.bio_project_accession}
            </Link>
          </p>

          {selectedProject.sra_accession && (
            <p>
              <strong>SRA Accession:</strong>{" "}
              <Link to={`/sra/${selectedProject.sra_accession}`}>
                {selectedProject.sra_accession}
              </Link>
            </p>
          )}

          <p><strong>Description:</strong><br />{selectedProject.description}</p>

          {selectedProject.data_type && (
            <p><strong>Data Type:</strong> {formatDataType(selectedProject.data_type)}</p>
          )}

          {selectedProject.organization_name && (
            <p><strong>Organization:</strong> {selectedProject.organization_name}</p>
          )}

          {selectedProject.relevance_agricultural !== null && (
            <p><strong>Relevance (Agricultural):</strong> {selectedProject.relevance_agricultural ? "Yes" : "No"}</p>
          )}

          {selectedProject.submission_id && (
            <p><strong>Submission ID:</strong> {selectedProject.submission_id}</p>
          )}

          {selectedProject.access_type && (
            <p><strong>Access Type:</strong> {selectedProject.access_type}</p>
          )}

          {selectedProject.tax_id && (
            <p>
              <strong>Taxonomy ID:</strong>{" "}
              <Link to={`/taxonomy/${selectedProject.tax_id}`}>
                {selectedProject.tax_id}
              </Link>
            </p>
          )}

          <h3>🔗 Related Links</h3>
          <ul className="ncbi-links">
            {selectedProject.tax_id && (
              <li>
                <a
                  href={`https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=${selectedProject.tax_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Taxonomy Page (NCBI)
                </a>
              </li>
            )}
            <li>
              <a
                href={`https://www.ncbi.nlm.nih.gov/bioproject/${selectedProject.bio_project_accession}`}
                target="_blank"
                rel="noreferrer"
              >
                BioProject Page (NCBI)
              </a>
            </li>
            {selectedProject.sra_accession && (
              <li>
                <a
                  href={`https://www.ncbi.nlm.nih.gov/sra/${selectedProject.sra_accession}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  SRA Project (NCBI)
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  }

  // ---------------- MAIN LIST VIEW ----------------
  return (
    <div className="bioproject-page">
      <div className="bioproject-layout">

        {/* FILTER PANEL */}
        <div className="bioproject-filters">
          <h3>Filters</h3>

          <h4>Data Type</h4>
          {["Assembly", "Map", "Raw sequence", "Random survey", "Transcriptome", "Other"].map((dt) => (
            <label key={dt}>
              <input
                type="checkbox"
                checked={selectedDataTypes.includes(dt)}
                onChange={() => {
                  setSelectedDataTypes((prev) =>
                    prev.includes(dt) ? prev.filter((d) => d !== dt) : [...prev, dt]
                  );
                  setCurrentPage(1);
                }}
              />
              {dt}
            </label>
          ))}

          <h4>Relevance (Agricultural)</h4>
          <label>
            <input
              type="radio"
              checked={selectedRelevance === null}
              onChange={() => setSelectedRelevance(null)}
            /> All
          </label>
          <label>
            <input
              type="radio"
              checked={selectedRelevance === true}
              onChange={() => setSelectedRelevance(true)}
            /> Yes
          </label>
          <label>
            <input
              type="radio"
              checked={selectedRelevance === false}
              onChange={() => setSelectedRelevance(false)}
            /> No
          </label>

          <h4>Organization</h4>
          <input
            type="text"
            value={organizationQuery}
            onChange={(e) => {
              setOrganizationQuery(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search organization..."
          />
        </div>

        {/* RESULTS LIST */}
        <div className="bioproject-content">
          <h3>📊 BioProject Data</h3>

          <input
            type="text"
            placeholder="Search by project title or accession..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="bioproject-search"
          />

          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            perPage={projectsPerPage}
            setCurrentPage={setCurrentPage}
          />

          {loading ? (
            <p>Loading BioProjects...</p>
          ) : (
            <>
              <div className="pagination-summary">
                {totalCount > 0
                  ? `Items: ${(currentPage - 1) * projectsPerPage + 1} to ${Math.min(currentPage * projectsPerPage, totalCount)} of ${totalCount}`
                  : "No BioProjects found."}
              </div>

              {bioProjects.map((project, idx) => (
                <div key={project.id} className="bioproject-item">
                  <h3 onClick={() => openProject(project)}>
                    <span className="project-index">
                      {idx + 1 + (currentPage - 1) * projectsPerPage}.
                    </span>{" "}
                    {project.project_title}
                  </h3>

                  {project.description && (
                    <p className="bioproject-desc">{shortDesc(project.description)}</p>
                  )}

                  {project.data_type && (
                    <div><strong>Data type:</strong> {formatDataType(project.data_type)}</div>
                  )}

                  {project.organization_name && (
                    <div><strong>Organization:</strong> {project.organization_name}</div>
                  )}

                  {project.sra_accession && (
                    <div><strong>SRA Accession:</strong> {project.sra_accession}</div>
                  )}

                  <div>
                    <strong>Accession:</strong>{" "}
                    <span
                      style={{ textDecoration: "underline", color: "blue", cursor: "pointer" }}
                      onClick={() => openProject(project)}
                    >
                      {project.bio_project_accession}
                    </span>
                  </div>

                  {project.tax_id && (
                    <div><strong>Taxonomy ID:</strong> {project.tax_id}</div>
                  )}
                </div>
              ))}

              <Pagination
                currentPage={currentPage}
                totalCount={totalCount}
                perPage={projectsPerPage}
                setCurrentPage={setCurrentPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BioProjectComponent;

